import { Component, OnInit } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, UntypedFormControl, Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';

import { SharedLoginSingletonService, SharedUsageDataSourceSingletonService } from '@huge-monorepo/shared/services';
import { ICustomerLoginService, IHIPLoginService } from '@huge-monorepo/shared/interfaces';
import { Router, ActivatedRoute } from '@angular/router';
import jwt_decode from 'jwt-decode';

@Component({
  selector: 'app-login-page',
  templateUrl: './login-page.component.html',
  styleUrls: ['./login-page.component.scss'],
})
export class LoginPageComponent implements OnInit {
  isHIP = this.sharedLoginService.getIsHIP();
  loginService = this.sharedLoginService.getService();

  customerPortal = false;

  hide = true;
  isSubmitted = false;
  toastr: any;
  customerIconColor: string;
  hipIconColor: string;
  otpRequired: boolean = false;

  // Contractor token from URL (optional)
  contractorToken: string | null = null;
  isContractorLink = false;

  isMobile = window.innerWidth <= 768;

  constructor(
    private sharedLoginService: SharedLoginSingletonService<ICustomerLoginService | IHIPLoginService>,
    private formBuilder: UntypedFormBuilder,
    private toastService: ToastrService,
    private router: Router,
    private usageDataSourceService: SharedUsageDataSourceSingletonService,
    private route: ActivatedRoute,
  ) { }

  ngOnInit(): void {
    this.updateSvgFill();
    this.contractorToken = this.route.snapshot.queryParamMap.get('token');
    if (this.contractorToken) {
      var decoded = jwt_decode<{ ContractorID?: number }>(this.contractorToken);
      this.isContractorLink = decoded['ContractorID'] ? true : false; // check if ContractorID exists in the token
    }
  }

  passwordControl = new UntypedFormControl('', [Validators.required]);
  emailControl = new UntypedFormControl('', [Validators.required, Validators.email]);
  otpControl = new UntypedFormControl('', [Validators.required]);

  ionicFormGroup = new UntypedFormGroup({
    emailControl: this.emailControl,
    passwordControl: this.passwordControl,
    otpControl: this.otpControl,
  });

  validations_form = this.formBuilder.group({
    emailControl: this.emailControl,
    passwordControl: this.passwordControl,
    otpControl: this.otpControl,
  });

  validation_messages = {
    passwordControl: [{ type: 'required', message: 'Password is required.' }],
    emailControl: [
      { type: 'required', message: 'Email is required.' },
      { type: 'email', message: 'Enter valid email.' },
    ],
    otpControl: [{ type: 'required', message: 'OTP is required.' }],
  };

  setSubmitting() {
    this.isSubmitted = true;
    this.emailControl.disable();
    this.passwordControl.disable();
  }

  setSubmittingFinished() {
    this.isSubmitted = false;
    this.emailControl.enable();
    this.passwordControl.enable();
  }

  submitLogin() {
    if (this.emailControl.invalid || this.passwordControl.invalid) {
      this.emailControl.markAsTouched();
      this.passwordControl.markAsTouched();
      return;
    }

    const checkObj = {
      Email: this.emailControl.value,
      Password: this.passwordControl.value,
    };

    this.setSubmitting();

    if (this.isHIP) {
      this.loginService.hasOTPEnabled(checkObj).subscribe({
        next: (res) => {
          if (res.error) {
            this.toastService.error(res.error);
            this.setSubmittingFinished();
            return;
          }

          if (res.OTPEnabled === true) {
            this.otpRequired = true;
            this.setSubmittingFinished();
          } else {
            this.loginLogic(null);
          }
        },
        error: () => {
          // This path shouldn't be hit, but fallback just in case
          this.toastService.error('Invalid login credentials. Please try again');
          this.setSubmittingFinished();
        },
      });
    } else {
      this.loginLogic(null);
    }
  }

  submitValidateOTP() {
    if (this.otpControl.invalid) {
      this.otpControl.markAsTouched();
      return;
    }

    const otpValue = this.otpControl.value;
    this.loginLogic(otpValue);
  }

  loginLogic(otp: string | null) {
    if (
      this.emailControl.value !== '' &&
      this.passwordControl.value !== '' &&
      this.emailControl.errors == null
    ) {
      this.setSubmitting();

      const obj = {
        Email: this.emailControl.value,
        Password: this.passwordControl.value,
        OTP: otp,
      };


      this.loginService.LoginRequest(obj, this.isHIP).subscribe({
        next: (response: { success: boolean; error?: string; isContractor?: boolean }) => {
          if (!response.success) {
            if (otp) {
              // Show OTP error toast
              this.toastService.error('The OTP you entered is incorrect.');
            }

            if (response?.error === 'registered') {
              this.passwordControl.setErrors({ registered: true });
            } else {
              this.passwordControl.setErrors({ incorrect: true });
            }

            this.passwordControl.markAsTouched();
            this.setSubmittingFinished();
          } else if (!response.isContractor) {
            this.usageDataSourceService.startTimer();
          }
        },
        error: (err) => {
          if (otp) {
            this.toastService.error('The OTP you entered is incorrect.');
          }

          this.passwordControl.setErrors({ incorrect: true });
          this.passwordControl.markAsTouched();
          this.setSubmittingFinished();
        },
      });
    } else {
      this.emailControl.markAsTouched();
      this.passwordControl.markAsTouched();
    }
  }

  ForgotPassword() {
    this.router.navigate(['/ForgotPassword', { email: this.emailControl.value }]);
  }

  showPassword() {
    this.hide = !this.hide;
  }

  SetCustomerPortal(res: boolean) {
    window.location.href = 'https://portal.hugeconnect.co.za/';
  }

  private updateSvgFill() {
    this.customerIconColor = this.customerPortal ? '#ffffff' : '#07062d';
    this.hipIconColor = this.customerPortal ? '#07062d' : '#ffffff';
  }

  Register() {
    if (this.contractorToken) {
      this.router.navigate(['/Register'], { queryParams: { token: this.contractorToken } });
    } else {
      this.router.navigate(['/Register']);
    }
  }

  LoginWithOTP() {
    this.router.navigate(
      ['/Contractor-Job-Completion/Login'],
      { queryParams: { token: this.contractorToken } }
    );
  }
}
